"use client";

import { motion } from "framer-motion";
import { ArrowUpRight, PenLine, Star } from "lucide-react";
import { useLanguage } from "./LanguageProvider";
import { GOOGLE_REVIEWS, type GoogleReview } from "@/lib/googleReviews";

const EASE = [0.22, 1, 0.36, 1] as const;

const TOPIC = {
  sales: { sv: "Bilköp", en: "Car purchase" },
  workshop: { sv: "Verkstad", en: "Workshop" },
  general: null,
} as const;

/** Five stars filled to `value` (e.g. 4.3 fills four and a third). */
function Stars({ value, className = "h-4 w-4" }: { value: number; className?: string }) {
  return (
    <span className="relative inline-flex" aria-hidden>
      <span className="flex text-foreground/20">
        {Array.from({ length: 5 }, (_, i) => <Star key={i} className={className} fill="currentColor" strokeWidth={0} />)}
      </span>
      <span className="absolute inset-0 flex overflow-hidden text-amber-400" style={{ width: `${(value / 5) * 100}%` }}>
        {Array.from({ length: 5 }, (_, i) => <Star key={i} className={`${className} shrink-0`} fill="currentColor" strokeWidth={0} />)}
      </span>
    </span>
  );
}

function GoogleG({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

export function GoogleReviews({
  className = "bg-muted/30",
  topics,
}: {
  className?: string;
  /** Only show these topics, e.g. ["workshop", "general"] on /verkstad. */
  topics?: GoogleReview["topic"][];
}) {
  const { language } = useLanguage();
  const sv = language === "sv";
  const { rating, count, writeReviewUrl, allReviewsUrl } = GOOGLE_REVIEWS;
  // One row: three reviews side by side on desktop, a swipe row on smaller screens.
  const reviews = (topics ? GOOGLE_REVIEWS.reviews.filter((r) => topics.includes(r.topic)) : GOOGLE_REVIEWS.reviews).slice(0, 3);
  const ratingText = rating.toLocaleString(sv ? "sv-SE" : "en-GB", { minimumFractionDigits: 1 });

  return (
    <section id="omdomen" className={`py-20 lg:py-28 ${className}`}>
      <div className="container mx-auto max-w-6xl px-6">
        <div className="mb-10 flex flex-col gap-8 lg:mb-14 lg:flex-row lg:items-end lg:justify-between">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="max-w-xl"
          >
            <p className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-muted-foreground">
              {sv ? "Omdömen" : "Reviews"}
            </p>
            <h2 className="font-display text-4xl font-bold leading-tight text-foreground lg:text-5xl">
              {sv ? "Vad våra kunder säger" : "What our customers say"}
            </h2>
          </motion.div>

          <motion.a
            href={allReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="group inline-flex items-center gap-4 self-start rounded-2xl border border-foreground/10 bg-background px-5 py-4 shadow-sm transition hover:shadow-md lg:self-auto"
            aria-label={sv ? `${ratingText} av 5 i betyg, ${count} omdömen på Google` : `Rated ${ratingText} out of 5, ${count} reviews on Google`}
          >
            <GoogleG className="h-8 w-8" />
            <span>
              <span className="flex items-center gap-2">
                <span className="font-display text-3xl font-bold text-foreground">{ratingText}</span>
                <Stars value={rating} className="h-5 w-5" />
              </span>
              <span className="text-sm text-muted-foreground group-hover:text-foreground">
                {sv ? `${count} omdömen på Google` : `${count} reviews on Google`}
              </span>
            </span>
          </motion.a>
        </div>

        {/* Phone and tablet: swipe sideways. Desktop: one row of three. */}
        <ul className="-mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:mx-0 lg:grid lg:grid-cols-3 lg:gap-6 lg:overflow-visible lg:px-0 lg:pb-0">
          {reviews.map((review, index) => {
            const topic = TOPIC[review.topic];
            return (
              <motion.li
                key={review.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.05 * index, ease: EASE }}
                className="flex w-[82%] shrink-0 snap-start flex-col rounded-2xl border border-foreground/10 bg-background p-6 shadow-sm sm:w-[45%] lg:w-auto"
              >
                <div className="mb-4 flex items-center justify-between gap-3">
                  <Stars value={review.rating} />
                  {topic && (
                    <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                      {topic[language]}
                    </span>
                  )}
                </div>
                <blockquote className="mb-6 flex-1 text-base leading-relaxed text-foreground" lang="sv">
                  ”{review.text}”
                </blockquote>
                <footer className="flex items-center gap-3 text-sm">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary" aria-hidden>
                    {review.name.charAt(0)}
                  </span>
                  <span className="font-medium text-foreground">{review.name}</span>
                  <GoogleG className="ml-auto h-4 w-4" />
                </footer>
              </motion.li>
            );
          })}
        </ul>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-10 flex flex-col items-start gap-6 rounded-2xl bg-primary px-6 py-8 text-primary-foreground md:flex-row md:items-center md:justify-between lg:px-10"
        >
          <div>
            <h3 className="mb-1 font-display text-2xl font-bold">
              {sv ? "Har du varit hos oss?" : "Have you visited us?"}
            </h3>
            <p className="text-primary-foreground/80">
              {sv
                ? "Berätta hur det gick. Ditt omdöme hjälper andra och oss att bli ännu bättre."
                : "Tell us how it went. Your review helps others, and helps us get even better."}
            </p>
          </div>
          <a
            href={writeReviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-neutral-900 transition hover:gap-3"
          >
            <PenLine className="h-4 w-4" aria-hidden />
            {sv ? "Lämna ett omdöme på Google" : "Leave a review on Google"}
          </a>
        </motion.div>

        <p className="mt-4 text-xs text-muted-foreground">
          {sv ? "Ett urval av omdömen från Google. " : "A selection of reviews from Google. "}
          <a href={allReviewsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 underline hover:text-foreground">
            {sv ? `Läs alla ${count} omdömen` : `Read all ${count} reviews`}
            <ArrowUpRight className="h-3 w-3" aria-hidden />
          </a>
        </p>
      </div>
    </section>
  );
}
